import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import { Textarea } from '@heidi/design-system/textarea';
import {
  TypographyCaption,
  TypographyH4,
  TypographyP2,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { AlertTriangle, Check, ChevronDown, FileText, Loader2 } from 'lucide-react';
import { useIntl } from 'react-intl';

import { voiceWorkspaceMessages as messages } from './workspace.messages';
import type { WorkspaceThreadDraft } from './workspace.types';

type InlineDraftCardProps = {
  draft: WorkspaceThreadDraft;
  expanded: boolean;
  onToggle: () => void;
  onContentChange: (content: string) => void;
  onCreate: () => void;
  onDiscard: () => void;
};

/**
 * Draft as a speak-thread card. Collapsed = chip; expanded = body + Create.
 * Superseded revisions are history-only (no Create).
 */
export function InlineDraftCard({
  draft,
  expanded,
  onToggle,
  onContentChange,
  onCreate,
  onDiscard,
}: InlineDraftCardProps) {
  const intl = useIntl();
  const { title, subtitle, content, status, version, superseded } = draft;
  const isStreaming = status === 'streaming';
  const isCreating = status === 'creating';
  const isCreated = status === 'created';
  const isHistory = Boolean(superseded) || isCreated;
  const isEditable = !isHistory && (status === 'ready' || status === 'creating');

  const statusBadge = superseded ? (
    <Badge color="neutral" size="sm">
      {intl.formatMessage(messages.statusSuperseded)}
    </Badge>
  ) : status === 'ready' ? (
    <Badge color="green" size="sm">
      {intl.formatMessage(messages.statusReady)}
    </Badge>
  ) : isCreated ? (
    <Badge color="green" size="sm">
      {intl.formatMessage(messages.created)}
    </Badge>
  ) : (
    <Badge color="neutral" size="sm">
      {intl.formatMessage(messages.statusDraft)}
    </Badge>
  );

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-xl border border-border/60 bg-surface px-3 py-2.5 text-start',
          'transition-colors hover:bg-surface-hover',
          superseded && 'opacity-70'
        )}
        data-testid="inline-draft-collapsed"
        data-superseded={superseded ? 'true' : undefined}
        aria-expanded={false}
      >
        <FileText className="size-4 shrink-0 text-text-tertiary" aria-hidden />
        <div className="min-w-0 flex-1">
          <TypographyP2 className="truncate text-text-primary">
            {version
              ? intl.formatMessage(messages.titleWithVersion, { title, version })
              : title}
          </TypographyP2>
          <TypographyCaption className="truncate text-text-tertiary">
            {superseded
              ? intl.formatMessage(messages.previousRevisionHint)
              : (subtitle ?? intl.formatMessage(messages.draftedByHeidi))}
          </TypographyCaption>
        </div>
        {statusBadge}
        <ChevronDown className="size-4 shrink-0 text-text-tertiary" aria-hidden />
      </button>
    );
  }

  return (
    <div
      className={cn(
        'flex w-full flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-sm',
        superseded && 'opacity-80'
      )}
      data-testid="inline-draft-expanded"
      data-superseded={superseded ? 'true' : undefined}
      aria-expanded
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex items-start gap-3 border-b border-border/60 px-4 py-3 text-start hover:bg-surface-hover/60"
      >
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-1.5">
            <HeidiLogoMark className="size-3 text-text-secondary" />
            <TypographyCaption className="text-text-tertiary">
              {version
                ? intl.formatMessage(messages.revisionWithVersion, {
                    revision: superseded ? 'previous' : 'current',
                    version,
                  })
                : intl.formatMessage(
                    superseded ? messages.previousRevision : messages.draftedByHeidi
                  )}
            </TypographyCaption>
          </div>
          <TypographyH4 className="text-balance">{title}</TypographyH4>
          {subtitle ? (
            <TypographyCaption className="mt-0.5 text-pretty text-text-secondary">
              {subtitle}
            </TypographyCaption>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2 pt-0.5">
          {statusBadge}
          <ChevronDown className="size-4 rotate-180 text-text-tertiary" aria-hidden />
        </div>
      </button>

      <div className="relative max-h-96 min-h-48 overflow-hidden">
        <Textarea
          value={content}
          readOnly={!isEditable}
          onChange={(event) => onContentChange(event.target.value)}
          aria-label={intl.formatMessage(messages.editDraft)}
          data-testid="inline-draft-content"
          wrapperClassName="size-full [&>span]:hidden [&>svg]:hidden"
          className={cn(
            'scrollbar-fade size-full min-h-48 resize-none border-0 bg-transparent px-4 py-3',
            'text-pretty text-sm leading-relaxed text-text-primary',
            'shadow-none focus-visible:ring-0',
            (isStreaming || isHistory) && 'text-text-secondary'
          )}
        />
        {isStreaming ? (
          <span
            className="pointer-events-none absolute bottom-3 start-4 inline-block h-4 w-0.5 animate-pulse rounded-full bg-text-tertiary motion-reduce:animate-none"
            aria-hidden
          />
        ) : null}
      </div>

      {isHistory ? (
        <div className="border-t border-border/60 px-4 py-2.5">
          <TypographyCaption className="text-text-tertiary">
            {superseded
              ? intl.formatMessage(messages.supersededFooter)
              : intl.formatMessage(messages.createdFooter)}
          </TypographyCaption>
        </div>
      ) : (
        <div className="flex items-center justify-end gap-2 border-t border-border/60 px-4 py-2.5">
          {draft.createError ? (
            <span
              role="alert"
              className="me-auto flex min-w-0 items-center gap-1.5"
              data-testid="inline-draft-create-error"
            >
              <AlertTriangle className="size-3.5 shrink-0 text-red-600" aria-hidden />
              <TypographyCaption className="truncate text-red-600">
                {intl.formatMessage(
                  draft.createError === 'no_session'
                    ? messages.createNoSession
                    : messages.createFailed
                )}
              </TypographyCaption>
            </span>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            onClick={onDiscard}
            disabled={isCreating}
            data-testid="inline-draft-discard"
          >
            {intl.formatMessage(messages.discard)}
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={onCreate}
            disabled={isStreaming || isCreating || isCreated}
            data-testid="inline-draft-create"
          >
            {isCreating ? (
              <Loader2 className="animate-spin motion-reduce:animate-none" />
            ) : isCreated ? (
              <Check />
            ) : null}
            {intl.formatMessage(isCreated ? messages.created : messages.create)}
          </Button>
        </div>
      )}
    </div>
  );
}
